import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { iniciarSesionGoogle } from "../services/authService";
import { useAuth } from "../context/AuthContext";
import { computeFirstAllowedRoute } from "../context/firstAllowedRoute";
import { FEATURE_BITS as FB, BITSET_LEN } from "../constants/permisos";

const normalize = (value = "") => "1" + String(value).padEnd(BITSET_LEN, "0").slice(1, BITSET_LEN);
let initializedClientId = "";
let activeCredentialHandler = null;

export default function GoogleSignIn() {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
  const button = useRef(null);
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [credentialActual, setCredentialActual] = useState("");
  const [seleccion, setSeleccion] = useState(null);

  const completarSesion = useCallback((data) => {
    const endpoint = normalize(data.endpoint);
    login({ email: data.email, rol: data.rol || "permanente", token: data.token });
    localStorage.setItem("endpoint", endpoint);
    localStorage.setItem("esSuperAdmin", JSON.stringify(!!data.esSuperAdmin));
    localStorage.setItem("perfilId", String(data.perfilId || ""));
    localStorage.setItem("tipoCuenta", data.tipoCuenta || "principal");
    window.dispatchEvent(new Event("auth-changed"));
    const hasBit = (bs, key) => bs[(FB[key] || 1) - 1] === "1";
    navigate(computeFirstAllowedRoute(endpoint, hasBit) || "/dashboard/admin", { replace: true });
  }, [login, navigate]);

  const elegirCuenta = async (opcion) => {
    setLoading(true); setError("");
    try {
      const data = await iniciarSesionGoogle(credentialActual, opcion);
      completarSesion(data);
      setSeleccion(null);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const handleCredential = useCallback(async ({ credential }) => {
    setLoading(true); setError("");
    try {
      const data = await iniciarSesionGoogle(credential);
      if (data.requiereSeleccionCuenta) {
        setCredentialActual(credential);
        setSeleccion(data);
      } else completarSesion(data);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [completarSesion]);

  useEffect(() => {
    if (!clientId) return undefined;
    let cancelled = false;
    let resizeObserver;
    activeCredentialHandler = handleCredential;
    const initialize = () => {
      if (cancelled || !window.google?.accounts?.id || !button.current) return;
      if (initializedClientId !== clientId) {
        window.google.accounts.id.initialize({
          client_id: clientId,
          auto_select: false,
          use_fedcm_for_button: true,
          button_auto_select: false,
          cancel_on_tap_outside: true,
          callback: response => activeCredentialHandler?.(response),
        });
        initializedClientId = clientId;
      }
      const availableWidth = Math.max(180, Math.min(320, Math.floor(button.current.getBoundingClientRect().width)));
      button.current.replaceChildren();
      window.google.accounts.id.renderButton(button.current, {
        theme: "outline",
        size: "large",
        width: availableWidth,
        text: "continue_with",
      });
    };
    if (window.google?.accounts?.id) initialize();
    else {
      const existing = document.querySelector('script[data-google-identity]');
      const script = existing || document.createElement("script");
      if (!existing) { script.src = "https://accounts.google.com/gsi/client"; script.async = true; script.defer = true; script.dataset.googleIdentity = "true"; document.head.appendChild(script); }
      script.addEventListener("load", initialize, { once: true });
    }
    if (button.current && typeof ResizeObserver !== "undefined") {
      let previousWidth = 0;
      resizeObserver = new ResizeObserver(([entry]) => {
        const width = Math.floor(entry.contentRect.width);
        if (width > 0 && width !== previousWidth) {
          previousWidth = width;
          initialize();
        }
      });
      resizeObserver.observe(button.current);
    }
    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
      if (activeCredentialHandler === handleCredential) activeCredentialHandler = null;
      window.google?.accounts?.id?.cancel();
    };
  }, [clientId, handleCredential]);

  if (!clientId) return <p className="text-xs text-gray-500 text-center">Acceso con Google pendiente de configuración.</p>;
  return <div className="space-y-2">
    <div ref={button} className={loading ? "google-signin-slot opacity-50 pointer-events-none" : "google-signin-slot"} />
    {loading && <p className="auth-notice auth-notice--info" role="status">Validando con Google…</p>}
    {error && <p className="auth-notice auth-notice--error" role="alert">{error}</p>}
    <p className="text-[11px] text-center text-gray-500">Google valida tu identidad; después eliges la cuenta de trabajo.</p>
    {seleccion && <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-neutral-900 text-white rounded-xl border border-emerald-500/40 p-5 w-full max-w-md shadow-2xl">
        <h3 className="text-xl font-bold text-emerald-400">¿A qué cuenta deseas entrar?</h3>
        <p className="text-sm text-neutral-300 mt-1 mb-4">Los permisos y datos de cada cuenta permanecen separados.</p>
        <div className="space-y-2">
          {(seleccion.cuentas || []).map((c) => <button key={c.perfilId} type="button"
            onClick={() => elegirCuenta({ perfilId: c.perfilId })}
            className="w-full text-left rounded-lg border border-neutral-700 bg-neutral-800 hover:border-emerald-500 p-3">
            <span className="block font-semibold">{c.nombreCuenta}</span>
            <span className="text-xs text-neutral-400">{c.tipo === "principal" ? "Cuenta principal" : "Acceso temporal"} · {c.rol}</span>
          </button>)}
          {seleccion.puedeCrearPrincipal && <button type="button"
            onClick={() => elegirCuenta({ crearCuentaPrincipal: true })}
            className="w-full text-left rounded-lg border border-cyan-600/60 bg-cyan-950/40 hover:border-cyan-400 p-3">
            <span className="block font-semibold text-cyan-300">Crear mi cuenta principal</span>
            <span className="text-xs text-neutral-300">Iniciar un ecosistema nuevo e independiente.</span>
          </button>}
        </div>
        <button type="button" onClick={() => setSeleccion(null)} className="mt-4 text-sm text-neutral-400 hover:text-white">Cancelar</button>
      </div>
    </div>}
  </div>;
}
